<template>
  <el-dialog
    :visible="visible"
    width="440px"
    custom-class="new-team-modal"
    :show-close="true"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    :modal-append-to-body="true"
    :append-to-body="true"
    :before-close="onCloseRequested"
    @close="onCloseRequested"
    @opened="onOpened"
  >
    <div slot="title" class="ntm__title">
      <span class="ntm__eyebrow">Step 3 — Your team</span>
      <span class="ntm__display">Add a new <b>team</b></span>
    </div>

    <div class="ntm__body">
      <p class="ntm__intro">
        Teams group people together so feedback flows to the right places. Use the
        functional name your company actually uses.
      </p>

      <label class="ntm__field">
        <span class="ntm__field-label">Team name</span>
        <input
          ref="input"
          v-model="draft"
          type="text"
          class="ntm__input"
          placeholder="e.g. Engineering, Marketing, Customer Success"
          maxlength="60"
          @keydown.enter.prevent="onSubmit"
          @keydown.esc.prevent="onCloseRequested"
        />
      </label>

      <p v-if="duplicateOfExisting" class="ntm__hint ntm__hint--error">
        That team already exists — pick it from the dropdown instead.
      </p>
    </div>

    <div slot="footer" class="ntm__footer">
      <button
        type="button"
        class="ntm__btn-secondary"
        @click="onCloseRequested"
      >Cancel</button>
      <button
        type="button"
        class="ntm__btn-primary"
        :disabled="!canSubmit"
        @click="onSubmit"
      >Add team</button>
    </div>
  </el-dialog>
</template>

<script>
/**
 * NewTeamModal — Element UI dialog for creating a new team during the
 * Step 3 (people) flow. The inline new-team editor in the people table
 * left almost no horizontal room to type, so we promoted the action to
 * a focused modal that gives the input proper breathing space.
 *
 * Props:
 *  - visible:        Boolean — dialog open state
 *  - existingTeams:  string[] — already-used team names (lower-case
 *                                comparison; used to surface a duplicate
 *                                hint without blocking submit because the
 *                                user might be re-typing on purpose).
 *
 * Emits:
 *  - close                  — user cancelled / closed the dialog
 *  - submit (teamName)      — primary CTA; teamName is the trimmed value
 *
 * Parent owns the people array — this component just collects the name.
 */
export default {
  name: 'NewTeamModal',
  props: {
    visible: { type: Boolean, default: false },
    existingTeams: { type: Array, default: () => [] },
  },
  data() {
    return {
      draft: '',
    }
  },
  computed: {
    trimmedDraft() {
      return String(this.draft || '').trim()
    },
    canSubmit() {
      return this.trimmedDraft.length > 0
    },
    duplicateOfExisting() {
      if (!this.trimmedDraft) return false
      const lower = this.trimmedDraft.toLowerCase()
      return (this.existingTeams || []).some(
        (t) => String(t || '').trim().toLowerCase() === lower
      )
    },
  },
  watch: {
    visible(open) {
      if (open) {
        // Reset state every time we open; if the user cancelled with a
        // half-typed name and re-opens later, we don't want to surprise
        // them with leftover text.
        this.draft = ''
      }
    },
  },
  methods: {
    onOpened() {
      // el-dialog fires 'opened' after the enter transition completes —
      // the safest moment to focus the input.
      this.$nextTick(() => {
        const el = this.$refs.input
        if (el && typeof el.focus === 'function') el.focus()
      })
    },
    onCloseRequested() {
      this.$emit('close')
    },
    onSubmit() {
      if (!this.canSubmit) return
      this.$emit('submit', this.trimmedDraft)
    },
  },
}
</script>

<style lang="scss">
/* el-dialog teleports to body, so styles can't be scoped — declare via
   the custom-class so they don't bleed elsewhere. */
.new-team-modal {
  border-radius: 18px !important;
  overflow: hidden;
  box-shadow:
    0 1px 2px rgba(30, 10, 60, 0.06),
    0 28px 56px -24px rgba(78, 20, 172, 0.18);
}
.new-team-modal .el-dialog__header {
  padding: 18px 22px 6px;
  border-bottom: 1px solid #ece8f4;
}
.new-team-modal .el-dialog__body {
  padding: 16px 22px 8px;
  /* Element UI defaults to word-break: break-all on dialog bodies, which
     breaks English mid-word ("U" + "se the…"). Restore natural wrapping. */
  word-break: normal;
  overflow-wrap: break-word;
}
.new-team-modal .el-dialog__footer {
  padding: 12px 22px 18px;
  border-top: 1px solid #ece8f4;
}

.new-team-modal .ntm__title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.new-team-modal .ntm__eyebrow {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #6C30CC;
}
.new-team-modal .ntm__display {
  font-family: 'Instrument Serif', 'Georgia', serif;
  font-style: italic;
  font-size: 22px;
  color: #1a1a2e;
  line-height: 1.1;

  b {
    font-weight: 700;
    font-style: italic;
  }
}

.new-team-modal .ntm__body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.new-team-modal .ntm__intro {
  margin: 0;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: #5a5a73;
}

.new-team-modal .ntm__field {
  display: block;
}
.new-team-modal .ntm__field-label {
  display: block;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 6px;
}
.new-team-modal .ntm__input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  height: 44px;
  padding: 10px 14px;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  background: #ffffff;
  border: 1.5px solid #ece8f4;
  border-radius: 12px;
  transition: border-color 140ms ease, box-shadow 140ms ease;

  &::placeholder {
    color: #a0a0b5;
    font-weight: 400;
  }

  &:hover:not(:focus) {
    border-color: #d5cde8;
  }

  &:focus {
    outline: none;
    border-color: #6C30CC;
    box-shadow: 0 0 0 3px rgba(108, 48, 204, 0.14);
  }
}

.new-team-modal .ntm__hint {
  margin: 0;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}
.new-team-modal .ntm__hint--error { color: #CC3030; }

.new-team-modal .ntm__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}
.new-team-modal .ntm__btn-secondary {
  appearance: none;
  background: transparent;
  border: 1px solid #ece8f4;
  border-radius: 10px;
  padding: 8px 14px;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #5a5a73;
  cursor: pointer;
  transition: background 140ms ease, border-color 140ms ease, color 140ms ease;

  &:hover {
    color: #1a1a2e;
    background: rgba(30, 10, 60, 0.04);
    border-color: #d5cde8;
  }
  &:focus-visible {
    outline: 2px solid #F7B924;
    outline-offset: 2px;
  }
}
.new-team-modal .ntm__btn-primary {
  appearance: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 18px;
  border-radius: 12px;
  background: linear-gradient(135deg, #6C30CC 0%, #4A1E8F 100%);
  color: #ffffff;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 13.5px;
  font-weight: 800;
  letter-spacing: 0.01em;
  line-height: 1;
  box-shadow:
    0 1px 2px rgba(30, 10, 60, 0.12),
    0 14px 32px -16px rgba(78, 20, 172, 0.55);
  transition: transform 120ms ease, box-shadow 140ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow:
      0 4px 14px rgba(108, 48, 204, 0.36),
      0 0 0 4px rgba(247, 185, 36, 0.18);
  }
  &:active:not(:disabled) { transform: translateY(0); }
  &:focus-visible {
    outline: 2px solid #F7B924;
    outline-offset: 2px;
  }
  &:disabled,
  &[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 1px 2px rgba(30, 10, 60, 0.08);
  }
}
</style>
